import { isNull, isUndefined, noop } from 'lodash/fp';
import React, { useCallback, useMemo, useState } from 'react';
import Modal from 'react-modal';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { setSelectedAgreementTypeId } from '../../admin/ai/few-shot/store';
import { AttestationPage, setAttestationPage } from '../../admin/attestations/store';
import { ClientDatasetView, setClientDatasetView } from '../../admin/client-datasets/store';
import { ClientRiskToleranceView, setClientRiskToleranceView } from '../../admin/client-risk-tolerance/store';
import { PlaybookPage, setPlaybookPage } from '../../admin/playbook/store';
import { systemMaintenanceOptions } from '../../admin/system-maintenance/store';
import { getPortfolioResponse, PortfolioAnalysisPage, setPortfolioAnalysisPageView } from '../../admin/portfolio-analytics/store';
import { ClientFeaturePermission, FeaturePermission } from '../../admin/users/store';
import { getClientHasFeaturePermission, getUser, getUserHasFeaturePermission, getUserRole, logoutStarted, toggleMyProfileModal } from '../../auth/login/store';
import { adminRoles, systemAdminRole, UserRole } from '../../constants/permittedRoles';
import { Agency, Attestation, Bookshelf, Calculator, Calendar, CheckList, Document, Form, MultiAnalytics, Notification, OfficeBuilding, Opinion, OpinionAnalytics, Robot, Settings, SmileyNeutral, Table, ThirdParty, User } from '../../shared/icons';
import { Chatbot } from '../../shared/icons/Chatbot';
import { LaboratoryScience } from '../../shared/icons/LaboratoryScience';
import { Scrollable } from '../../shared/scrollable/Scrollable';
import styles from '../HomeWrapper.module.scss';
import { getRightMenuOpen, toggleRightMenu } from '../store';
import { LogoutButton } from './LogoutButton';
import { MenuTileId, MenuTileProps, TilesMenu } from './MenuTile';
import { MinifiedProfile } from './MinifiedProfile';
import { NavigationPath, Path } from './NavigationPath';

export const RightMenu: React.FC = () => {
    const [selectedTile, setSelectedTile] = useState<MenuTileId | null>(null);
    const user = useAppSelector(getUser);
    const userRole = useAppSelector(getUserRole);
    const isOpen = useAppSelector(getRightMenuOpen);
    const portfolioResponse = useAppSelector(getPortfolioResponse);

    const dispatch = useAppDispatch();

    const closeMenu = useCallback(() => {
        dispatch(toggleRightMenu(false));
        setSelectedTile(null);
    }, [dispatch]);

    const logout = useCallback(() => {
        closeMenu();
        dispatch(logoutStarted());
    }, [dispatch, closeMenu]);

    const openAttestations = useCallback(() => {
        dispatch(setAttestationPage(AttestationPage.SELECT));
        closeMenu();
    }, [dispatch, closeMenu]);

    const viewMyProfile = useCallback(() => {
        closeMenu();
        dispatch(toggleMyProfileModal(true));
    }, [dispatch, closeMenu]);

    const openPlaybooks = useCallback(() => {
        dispatch(setPlaybookPage(PlaybookPage.SELECT));
        closeMenu();
    }, [closeMenu, dispatch]);

    const openClientDatasets = useCallback(() => {
        dispatch(setClientDatasetView(ClientDatasetView.LIST, null, false));
        closeMenu();
    }, [dispatch, closeMenu]);

    const openClientRiskTolerance = useCallback(() => {
        dispatch(setClientRiskToleranceView(ClientRiskToleranceView.LIST, null, false));
        closeMenu();
    }, [dispatch, closeMenu]);

    const openPortfolioAnalytics = useCallback(() => {
        if (isNull(portfolioResponse)) {
            dispatch(setPortfolioAnalysisPageView(PortfolioAnalysisPage.SELECT));
        }
        closeMenu();
    }, [dispatch, portfolioResponse, closeMenu]);

    const openDocumentFewShot = useCallback(() => {
        dispatch(setSelectedAgreementTypeId(null));
        closeMenu();
    }, [closeMenu, dispatch]);

    const isPermitted = useCallback((permittedRoles: UserRole[]) => permittedRoles.includes(userRole!), [userRole]);
    const isAdmin = isPermitted(adminRoles);
    const isSystemAdmin = isPermitted(systemAdminRole);
    const hasPlaybookManagementPermission = useAppSelector(getUserHasFeaturePermission([FeaturePermission.PLAYBOOK_MANAGEMENT]));
    const hasEntityManagementPermission = useAppSelector(getUserHasFeaturePermission([FeaturePermission.ENTITY_MANAGEMENT]));
    const hasGroupDocumentManagementFeaturePermission = useAppSelector(getUserHasFeaturePermission([FeaturePermission.DOCUMENT_GROUP_MANAGEMENT]));
    const hasSubCounterpartyTypePermission = useAppSelector(getUserHasFeaturePermission([FeaturePermission.SUB_COUNTERPARTY_TYPE]));
    const hasClientDocumentPermission = useAppSelector(getClientHasFeaturePermission([ClientFeaturePermission.DOCUMENT_MODULE]));
    const hasOpinionPermission = useAppSelector(getClientHasFeaturePermission([ClientFeaturePermission.OPINION_MODULE]));
    const adminMenuVisible = useMemo(() => isAdmin || hasEntityManagementPermission || hasPlaybookManagementPermission || hasSubCounterpartyTypePermission, [isAdmin, hasEntityManagementPermission, hasPlaybookManagementPermission, hasSubCounterpartyTypePermission]);

    const systemMaintenanceIds = useMemo(() => user && user.systemMaintenanceIds || [], [user]);

    const menuTileIdsDisabledForMaintenance = useMemo(() => systemMaintenanceIds.reduce((acc: string[], cur) => {
        if (systemMaintenanceOptions[cur] && !isUndefined(systemMaintenanceOptions[cur].menuTileId)) {
            acc.push(systemMaintenanceOptions[cur].menuTileId!);
        }
        return acc;
    }, []), [systemMaintenanceIds]);

    const disabledTileMaintenanceTooltip = useMemo(() => systemMaintenanceIds.reduce((acc: { menuTileId: string, tooltip: string }[], cur) => {
        if (systemMaintenanceOptions[cur] && !isUndefined(systemMaintenanceOptions[cur].menuTileId)) {
            acc.push({ menuTileId: systemMaintenanceOptions[cur].menuTileId!, tooltip: systemMaintenanceOptions[cur].tooltipMessage });
        }
        return acc;
    }, []), [systemMaintenanceIds]);

    const checkTileDisabled = useCallback((menuTileId: MenuTileId, requiredPermission: boolean | undefined = undefined) => {
        if (!isUndefined(requiredPermission) && !requiredPermission) {
            return true;
        }
        return menuTileIdsDisabledForMaintenance.includes(menuTileId);
    }, [menuTileIdsDisabledForMaintenance]);

    const getDisabledTileTooltip = useCallback((tile: MenuTileId) => disabledTileMaintenanceTooltip.find(({ menuTileId }) => menuTileId === tile)?.tooltip, [disabledTileMaintenanceTooltip]);

    const handleTileClick = useCallback((tileId: MenuTileId | null) => {
        if (isNull(tileId)) {
            setSelectedTile(tileId);
        }
        if (!isNull(tileId) && menuTileIdsDisabledForMaintenance.includes(tileId)) {
            return noop;
        }
        switch (tileId) {
            case MenuTileId.USER_MANAGEMENT:
            case MenuTileId.SYSTEM_MANAGEMENT:
            case MenuTileId.SYSTEM_AI_MANAGEMENT:
            case MenuTileId.SYSTEM_DOCUMENT_MANAGEMENT:
                return setSelectedTile(tileId);
            case MenuTileId.COUNTERPARTY_MANAGEMENT: {
                if ((hasSubCounterpartyTypePermission && hasOpinionPermission) && hasEntityManagementPermission) {
                    return setSelectedTile(tileId);
                }
                return closeMenu();
            }
            case MenuTileId.DOCUMENT_MANAGEMENT: {
                if (isAdmin) {
                    return setSelectedTile(tileId);
                }
                return closeMenu();
            }
            case MenuTileId.PLAYBOOK_MANAGEMENT:
                return openPlaybooks();
            case MenuTileId.ATTESTATIONS:
                return openAttestations();
            case MenuTileId.CLIENT_DATASETS:
            case MenuTileId.DOCUMENT_GROUP_MANAGEMENT:
                return openClientDatasets();
            case MenuTileId.CLIENT_RISK_TOLERANCE:
                return openClientRiskTolerance();
            case MenuTileId.SYSTEM_AI_FEW_SHOT:
                return openDocumentFewShot();
            default:
                return closeMenu();
        }
    }, [menuTileIdsDisabledForMaintenance, openPlaybooks, openAttestations, openClientDatasets, openClientRiskTolerance, closeMenu, hasSubCounterpartyTypePermission, hasOpinionPermission, hasEntityManagementPermission, isAdmin, openDocumentFewShot]);

    const allAdminChildTiles: MenuTileProps[] = useMemo(() => ([
        {
            id: MenuTileId.ENTITY_MANAGEMENT,
            label: 'Entity Management',
            onClick: handleTileClick,
            shouldDisplay: hasEntityManagementPermission,
            icon: OfficeBuilding,
            path: '/admin/entities',
            parent: MenuTileId.COUNTERPARTY_MANAGEMENT,
            disabled: checkTileDisabled(MenuTileId.ENTITY_MANAGEMENT),
            disabledTooltip: getDisabledTileTooltip(MenuTileId.ENTITY_MANAGEMENT)
        },
        {
            id: MenuTileId.SUB_COUNTERPARTY_MANAGEMENT,
            label: 'Sub Counterparties',
            onClick: handleTileClick,
            shouldDisplay: hasSubCounterpartyTypePermission && hasOpinionPermission,
            icon: Opinion,
            path: '/admin/sub-counterparties',
            parent: MenuTileId.COUNTERPARTY_MANAGEMENT,
            disabled: checkTileDisabled(MenuTileId.SUB_COUNTERPARTY_MANAGEMENT),
            disabledTooltip: getDisabledTileTooltip(MenuTileId.SUB_COUNTERPARTY_MANAGEMENT)
        },
        {
            id: MenuTileId.CLIENT_DATASETS,
            label: 'Datasets',
            onClick: handleTileClick,
            shouldDisplay: isAdmin,
            icon: Table,
            path: '/admin/datasets',
            parent: MenuTileId.DOCUMENT_MANAGEMENT,
            disabled: checkTileDisabled(MenuTileId.CLIENT_DATASETS),
            disabledTooltip: getDisabledTileTooltip(MenuTileId.CLIENT_DATASETS)
        },
        {
            id: MenuTileId.CLIENT_RISK_TOLERANCE,
            label: 'Risk Tolerance',
            onClick: handleTileClick,
            shouldDisplay: isAdmin,
            icon: SmileyNeutral,
            path: '/admin/risk-tolerance',
            parent: MenuTileId.DOCUMENT_MANAGEMENT,
            disabled: checkTileDisabled(MenuTileId.CLIENT_RISK_TOLERANCE),
            disabledTooltip: getDisabledTileTooltip(MenuTileId.CLIENT_RISK_TOLERANCE)
        },
        {
            id: MenuTileId.CLIENT_CALCULATOR_PROMPTS,
            label: 'Calculator Prompts',
            onClick: handleTileClick,
            shouldDisplay: isAdmin,
            icon: Calculator,
            path: '/admin/calculator-prompts',
            parent: MenuTileId.DOCUMENT_MANAGEMENT,
            disabled: checkTileDisabled(MenuTileId.CLIENT_CALCULATOR_PROMPTS),
            disabledTooltip: getDisabledTileTooltip(MenuTileId.CLIENT_CALCULATOR_PROMPTS)
        },
        {
            id: MenuTileId.DOCUMENT_GROUP_MANAGEMENT,
            label: 'Document Groups',
            onClick: handleTileClick,
            shouldDisplay: isAdmin,
            icon: Document,
            path: '/admin/document-groups',
            parent: MenuTileId.DOCUMENT_MANAGEMENT,
            disabled: checkTileDisabled(MenuTileId.DOCUMENT_GROUP_MANAGEMENT),
            disabledTooltip: getDisabledTileTooltip(MenuTileId.DOCUMENT_GROUP_MANAGEMENT)
        },
        {
            id: MenuTileId.PORTFOLIO_ANALYTICS,
            label: 'Intelligent Portfolio Analysis',
            onClick: openPortfolioAnalytics,
            shouldDisplay: isAdmin,
            icon: Robot,
            path: '/admin/intelligent-portfolio-analysis',
            parent: MenuTileId.DOCUMENT_MANAGEMENT,
            disabled: checkTileDisabled(MenuTileId.PORTFOLIO_ANALYTICS),
            disabledTooltip: getDisabledTileTooltip(MenuTileId.PORTFOLIO_ANALYTICS)
        },
        {
            id: MenuTileId.ACCESS_GROUPS,
            label: 'Access Groups',
            onClick: handleTileClick,
            shouldDisplay: isAdmin,
            icon: Agency,
            path: '/admin/access-groups',
            parent: MenuTileId.USER_MANAGEMENT,
            disabled: checkTileDisabled(MenuTileId.ACCESS_GROUPS),
            disabledTooltip: getDisabledTileTooltip(MenuTileId.ACCESS_GROUPS)
        },
        {
            id: MenuTileId.ROLES,
            label: 'Roles',
            onClick: handleTileClick,
            shouldDisplay: isAdmin,
            icon: ThirdParty,
            path: '/admin/roles',
            parent: MenuTileId.USER_MANAGEMENT,
            disabled: checkTileDisabled(MenuTileId.ROLES),
            disabledTooltip: getDisabledTileTooltip(MenuTileId.ROLES)
        },
        {
            id: MenuTileId.USERS,
            label: 'Users',
            onClick: handleTileClick,
            shouldDisplay: isAdmin,
            icon: User,
            path: '/admin/users',
            parent: MenuTileId.USER_MANAGEMENT,
            disabled: checkTileDisabled(MenuTileId.USERS),
            disabledTooltip: getDisabledTileTooltip(MenuTileId.USERS)
        },
        {
            id: MenuTileId.SYSTEM_AI_MANAGEMENT,
            label: 'AI',
            onClick: handleTileClick,
            shouldDisplay: isSystemAdmin,
            icon: Robot,
            parent: MenuTileId.SYSTEM_MANAGEMENT,
            disabled: checkTileDisabled(MenuTileId.USER_MANAGEMENT),
            disabledTooltip: getDisabledTileTooltip(MenuTileId.USER_MANAGEMENT)
        },
        {
            id: MenuTileId.SYSTEM_DOCUMENT_MANAGEMENT,
            label: 'Dataset Management',
            onClick: handleTileClick,
            shouldDisplay: isSystemAdmin,
            icon: Form,
            parent: MenuTileId.SYSTEM_MANAGEMENT,
            disabled: checkTileDisabled(MenuTileId.USER_MANAGEMENT),
            disabledTooltip: getDisabledTileTooltip(MenuTileId.USER_MANAGEMENT)
        },
        {
            id: MenuTileId.SYSTEM_AI_DATA_EXTRACTION,
            label: 'Data Extraction',
            onClick: handleTileClick,
            shouldDisplay: isSystemAdmin,
            icon: Robot,
            path: '/system/ai/data-extraction',
            parent: MenuTileId.SYSTEM_AI_MANAGEMENT,
            disabled: checkTileDisabled(MenuTileId.USER_MANAGEMENT),
            disabledTooltip: getDisabledTileTooltip(MenuTileId.USER_MANAGEMENT)
        },
        {
            id: MenuTileId.SYSTEM_AI_PLAYGROUND,
            label: 'Playground',
            onClick: handleTileClick,
            shouldDisplay: isSystemAdmin,
            icon: LaboratoryScience,
            path: '/system/ai/playground',
            parent: MenuTileId.SYSTEM_AI_MANAGEMENT,
            disabled: checkTileDisabled(MenuTileId.USER_MANAGEMENT),
            disabledTooltip: getDisabledTileTooltip(MenuTileId.USER_MANAGEMENT)
        },
        {
            id: MenuTileId.SYSTEM_AI_ANALYTICS,
            label: 'Chat Bot Analytics',
            onClick: handleTileClick,
            shouldDisplay: isSystemAdmin,
            icon: Chatbot,
            path: '/system/ai/chat-bot',
            parent: MenuTileId.SYSTEM_AI_MANAGEMENT
        },
        {
            id: MenuTileId.SYSTEM_AI_FEW_SHOT,
            label: 'Document Few Shot',
            onClick: handleTileClick,
            shouldDisplay: isSystemAdmin,
            icon: Document,
            path: '/system/ai/few-shot',
            parent: MenuTileId.SYSTEM_AI_MANAGEMENT
        },
        {
            id: MenuTileId.SYSTEM_DATASETS,
            label: 'Datasets',
            onClick: handleTileClick,
            shouldDisplay: isSystemAdmin,
            icon: Table,
            path: '/system/datasets',
            parent: MenuTileId.SYSTEM_DOCUMENT_MANAGEMENT
        },
        {
            id: MenuTileId.SYSTEM_RISK_TOLERANCE,
            label: 'Risk Tolerance',
            onClick: handleTileClick,
            shouldDisplay: isSystemAdmin,
            icon: SmileyNeutral,
            path: '/system/risk-tolerance',
            parent: MenuTileId.SYSTEM_DOCUMENT_MANAGEMENT
        },
        {
            id: MenuTileId.SYSTEM_CALCULATOR_MANAGER,
            label: 'Calculator',
            onClick: handleTileClick,
            shouldDisplay: isSystemAdmin,
            icon: Calculator,
            path: '/system/calculator',
            parent: MenuTileId.SYSTEM_DOCUMENT_MANAGEMENT
        },
        {
            id: MenuTileId.SYSTEM_DOCUMENT_NAME,
            label: 'Documents',
            onClick: handleTileClick,
            shouldDisplay: isSystemAdmin,
            icon: Document,
            path: '/system/documents',
            parent: MenuTileId.SYSTEM_MANAGEMENT
        },
        {
            id: MenuTileId.SYSTEM_OPINION_ANALYTICS,
            label: 'Opinion Analytics',
            onClick: handleTileClick,
            shouldDisplay: isSystemAdmin,
            icon: OpinionAnalytics,
            path: '/system/opinions',
            parent: MenuTileId.SYSTEM_MANAGEMENT
        },
        {
            id: MenuTileId.SYSTEM_DROPDOWN_LISTS,
            label: 'Dropdown Lists',
            onClick: handleTileClick,
            shouldDisplay: isSystemAdmin,
            icon: CheckList,
            path: '/system/dropdown-lists',
            parent: MenuTileId.SYSTEM_MANAGEMENT
        },
        {
            id: MenuTileId.SYSTEM_CLIENT_MANAGEMENT,
            label: 'Clients',
            onClick: handleTileClick,
            shouldDisplay: isSystemAdmin,
            icon: CheckList,
            path: '/system/clients',
            parent: MenuTileId.SYSTEM_MANAGEMENT
        },
        {
            id: MenuTileId.SYSTEM_NOTIFICATION,
            label: 'Send Notification',
            onClick: handleTileClick,
            shouldDisplay: isSystemAdmin,
            icon: Notification,
            path: '/system/notification',
            parent: MenuTileId.SYSTEM_MANAGEMENT
        },
        {
            id: MenuTileId.SYSTEM_MAINTENANCE,
            label: 'System Maintenance',
            onClick: handleTileClick,
            shouldDisplay: isSystemAdmin,
            icon: Settings,
            path: '/system/maintenance',
            parent: MenuTileId.SYSTEM_MANAGEMENT
        }
    ]), [handleTileClick, hasEntityManagementPermission, checkTileDisabled, getDisabledTileTooltip, hasSubCounterpartyTypePermission, hasOpinionPermission, isAdmin, openPortfolioAnalytics, isSystemAdmin]);

    const childTiles = useMemo(() => allAdminChildTiles.filter(({ parent }) => parent === selectedTile), [selectedTile, allAdminChildTiles]);

    const counterpartyPath = useMemo(() => {
        if ((hasSubCounterpartyTypePermission && hasOpinionPermission) && hasEntityManagementPermission) {
            return undefined;
        }
        return (hasSubCounterpartyTypePermission && hasOpinionPermission) ? '/admin/sub-counterparties' : '/admin/entities';
    }, [hasSubCounterpartyTypePermission, hasOpinionPermission, hasEntityManagementPermission]);

    const groupTiles: MenuTileProps[] = useMemo(() => ([
        {
            id: MenuTileId.USER_MANAGEMENT,
            label: 'User Management',
            onClick: handleTileClick,
            shouldDisplay: isAdmin,
            icon: User,
            disabled: checkTileDisabled(MenuTileId.USER_MANAGEMENT),
            disabledTooltip: getDisabledTileTooltip(MenuTileId.USER_MANAGEMENT)
        },
        {
            id: MenuTileId.COUNTERPARTY_MANAGEMENT,
            label: 'Counterparty Management',
            onClick: handleTileClick,
            shouldDisplay: (hasSubCounterpartyTypePermission && hasOpinionPermission) || hasEntityManagementPermission,
            icon: OfficeBuilding,
            path: counterpartyPath,
            disabled: checkTileDisabled(MenuTileId.COUNTERPARTY_MANAGEMENT),
            disabledTooltip: getDisabledTileTooltip(MenuTileId.COUNTERPARTY_MANAGEMENT)
        },
        {
            id: MenuTileId.DOCUMENT_MANAGEMENT,
            label: 'Document Management',
            onClick: handleTileClick,
            shouldDisplay: isAdmin || (hasGroupDocumentManagementFeaturePermission && hasClientDocumentPermission),
            icon: Document,
            path: isAdmin ? undefined : '/admin/document-groups',
            disabled: checkTileDisabled(MenuTileId.DOCUMENT_MANAGEMENT),
            disabledTooltip: getDisabledTileTooltip(MenuTileId.DOCUMENT_MANAGEMENT)
        },
        {
            id: MenuTileId.PLAYBOOK_MANAGEMENT,
            label: 'Playbook Management',
            onClick: handleTileClick,
            shouldDisplay: isAdmin || hasPlaybookManagementPermission,
            icon: Bookshelf,
            path: '/admin/playbook',
            disabled: checkTileDisabled(MenuTileId.PLAYBOOK_MANAGEMENT),
            disabledTooltip: getDisabledTileTooltip(MenuTileId.PLAYBOOK_MANAGEMENT)
        },
        {
            id: MenuTileId.WORKFLOW,
            label: 'Workflow',
            onClick: handleTileClick,
            shouldDisplay: isAdmin,
            icon: Calendar,
            path: '/admin/workflow',
            disabled: checkTileDisabled(MenuTileId.WORKFLOW),
            disabledTooltip: getDisabledTileTooltip(MenuTileId.WORKFLOW)
        },
        {
            id: MenuTileId.ANALYTICS,
            label: 'Analytics',
            onClick: handleTileClick,
            shouldDisplay: isAdmin,
            icon: MultiAnalytics,
            path: '/admin/analytics',
            disabled: checkTileDisabled(MenuTileId.ANALYTICS),
            disabledTooltip: getDisabledTileTooltip(MenuTileId.ANALYTICS)
        },
        {
            id: MenuTileId.ATTESTATIONS,
            label: 'Attestations',
            onClick: handleTileClick,
            shouldDisplay: isAdmin,
            icon: Attestation,
            path: '/admin/attestation',
            disabled: checkTileDisabled(MenuTileId.ATTESTATIONS),
            disabledTooltip: getDisabledTileTooltip(MenuTileId.ATTESTATIONS)
        },
        {
            id: MenuTileId.SYSTEM_MANAGEMENT,
            label: 'System Management',
            onClick: handleTileClick,
            shouldDisplay: isSystemAdmin,
            icon: Settings
        }
    ]), [handleTileClick, isAdmin, checkTileDisabled, getDisabledTileTooltip, hasSubCounterpartyTypePermission, hasOpinionPermission, hasEntityManagementPermission, counterpartyPath, hasGroupDocumentManagementFeaturePermission, hasClientDocumentPermission, hasPlaybookManagementPermission, isSystemAdmin]);

    const allTiles = useMemo(() => [...allAdminChildTiles, ...groupTiles], [allAdminChildTiles, groupTiles]);

    const scrollableHeight = useMemo(() => `calc(100% - ${isNull(selectedTile) ? 1 : 31}px)`, [selectedTile]);

    const getAdminPath = useCallback((adminGroupId: MenuTileId): Path[] => {
        let adminPath: Path = { id: null, label: 'Admin' };
        switch (adminGroupId) {
            case MenuTileId.USER_MANAGEMENT:
                return [adminPath, { id: MenuTileId.USER_MANAGEMENT, label: 'User Management' }];
            case MenuTileId.COUNTERPARTY_MANAGEMENT:
                return [adminPath, { id: MenuTileId.COUNTERPARTY_MANAGEMENT, label: 'Entity Management' }];
            case MenuTileId.DOCUMENT_MANAGEMENT:
                return [adminPath, { id: MenuTileId.DOCUMENT_MANAGEMENT, label: 'Document Management' }];
            case MenuTileId.PLAYBOOK_MANAGEMENT:
                return [adminPath, { id: MenuTileId.PLAYBOOK_MANAGEMENT, label: 'Playbook Management' }];
            case MenuTileId.WORKFLOW:
                return [adminPath, { id: MenuTileId.WORKFLOW, label: 'Workflow' }];
            case MenuTileId.ANALYTICS:
                return [adminPath, { id: MenuTileId.ANALYTICS, label: 'Analytics' }];
            case MenuTileId.SYSTEM_MANAGEMENT:
                return [adminPath, { id: MenuTileId.SYSTEM_MANAGEMENT, label: 'System Management' }];
            case MenuTileId.SYSTEM_AI_MANAGEMENT:
                return [adminPath, { id: MenuTileId.SYSTEM_MANAGEMENT, label: 'System Management' }, { id: MenuTileId.SYSTEM_AI_MANAGEMENT, label: 'AI' }];
            case MenuTileId.SYSTEM_DOCUMENT_MANAGEMENT:
                return [adminPath, { id: MenuTileId.SYSTEM_MANAGEMENT, label: 'System Management' }, { id: MenuTileId.SYSTEM_DOCUMENT_MANAGEMENT, label: 'Dataset Management' }];
            default:
                return [adminPath];
        }
    }, []);

    return (
        <Modal
            isOpen={isOpen}
            className={styles.rightMenu}
            ariaHideApp={false}
            style={{ overlay: { display: 'flex', zIndex: 10 } }}
            shouldCloseOnOverlayClick
            shouldCloseOnEsc
            onRequestClose={closeMenu}
        >
            <div data-testid='right-menu-wrapper' className={styles.rightMenuWrapper}>
                <MinifiedProfile user={user} openProfile={viewMyProfile} />
                {adminMenuVisible &&
                    <div className={styles.adminWrapper}>
                        <NavigationPath getPath={getAdminPath} selectPath={setSelectedTile} selectedTile={selectedTile} />
                        <div className={styles.scrollableWrapper} style={{ height: scrollableHeight }}>
                            <Scrollable>
                                <TilesMenu
                                    tiles={groupTiles}
                                    selected={selectedTile}
                                    childTiles={childTiles}
                                    allTiles={allTiles}
                                    justifyContent='flex-end'
                                    sortAlphabetically
                                />
                            </Scrollable>
                        </div>
                    </div>
                }
            </div>
            <LogoutButton logout={logout} />
        </Modal >
    );
};
